import { useWatch, type Control } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  buildMintrouteDetailsPreview,
  type MintrouteCatalogueNames,
} from '../lib/mintroute-details';
import type { MapProvidersValues } from '../schemas/mapping.schema';

const MINTROUTE_IDS = [
  { key: 'denominationId', label: 'Mintroute denomination id' },
  { key: 'brandId', label: 'Mintroute brand id' },
] as const;

/**
 * The Mintroute catalogue details of one mapping row: the category name (pre-filled,
 * editable; stored as Category and CategoryId), Mintroute's brand and denomination
 * ids (entered), wallet (default USD) and VAT (default NO). Brand and denomination
 * names are copied from ours and the contract price is the cost, so they are
 * shown, not typed. The EAN is the row's product id field. A live preview shows
 * the details row the server will store.
 */
export function MintrouteFields({
  control,
  index,
  names,
  disabled,
}: {
  control: Control<MapProvidersValues>;
  index: number;
  names: MintrouteCatalogueNames;
  disabled: boolean;
}) {
  const [ean, cost, details] = useWatch({
    control,
    name: [`items.${index}.productIdentifier`, `items.${index}.cost`, `items.${index}.mintroute`],
  });

  return (
    <div className="border-border space-y-4 rounded-md border border-dashed p-3 sm:col-span-2">
      <div>
        <p className="text-sm font-medium">Mintroute catalogue details</p>
        <p className="text-muted-foreground text-xs">
          Copied from ours: brand <strong>{names.brand ?? '—'}</strong>, denomination{' '}
          <strong>{names.denomination ?? '—'}</strong>. The contract price is the cost.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          control={control}
          name={`items.${index}.mintroute.category`}
          render={({ field }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>Category</FormLabel>
              <FormControl>
                <Input {...field} placeholder={names.category ?? undefined} disabled={disabled} />
              </FormControl>
              <FormDescription>
                Free text, saved exactly as entered in both Category and CategoryId. Pre-filled with
                our category{names.category ? ` (${names.category})` : ''}; Mintroute's may differ.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {MINTROUTE_IDS.map(({ key, label }) => (
          <FormField
            key={key}
            control={control}
            name={`items.${index}.mintroute.${key}`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={Number.isNaN(field.value) ? '' : field.value}
                    onChange={(event) => field.onChange(event.target.valueAsNumber)}
                    type="number"
                    min={1}
                    step={1}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}

        <FormField
          control={control}
          name={`items.${index}.mintroute.wallet`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Wallet</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                  maxLength={3}
                  className="uppercase"
                  disabled={disabled}
                />
              </FormControl>
              <FormDescription>Default USD.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name={`items.${index}.mintroute.vat`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>VAT</FormLabel>
              <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                <FormControl>
                  <SelectTrigger aria-label="VAT">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="NO">NO</SelectItem>
                  <SelectItem value="YES">YES</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>Default NO.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <details>
        <summary className="text-muted-foreground cursor-pointer text-xs">
          Preview of the stored details
        </summary>
        <pre
          className="bg-muted mt-2 overflow-x-auto rounded p-2 font-mono text-[11px] whitespace-pre-wrap"
          aria-label="Mintroute details preview"
        >
          {buildMintrouteDetailsPreview(names, ean ?? '', details, cost)}
        </pre>
      </details>
    </div>
  );
}
